<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { AdvancedMarker, GoogleMap } from 'vue3-google-map';
import { useI18n } from 'vue-i18n';

import { buildPriceMarker } from '@/helpers/googleMapsHelper.ts';
import { getImageUrlWithCustomWidth } from '@/helpers/imageHelpers.ts';
import type { LocaleSchema } from '@/locales/index.ts';
import type { Project } from '@/typings/projects/Project.ts';
import { ListingTypeEnum } from '@/enums/ListingType';
import { useProject } from '@/composables/useProject.ts';
import { useRentalLocalizedUrls } from '@/composables/useRentalLocalizedUrls';
import { usePurchaseLocalizedUrls } from '@/composables/usePurchaseLocalizedUrls';
import { useCoordinates } from '@/composables/useCoordinates.ts';
  
const { t, locale } = useI18n<{ message: LocaleSchema }>({
  useScope: 'global',
});

const props = defineProps<{
  projects: Project[];
  listingType: ListingTypeEnum;
  ariaLabel?: string;
}>();

const model = defineModel<Project>();
const hoveredModel = defineModel<Project>('hoveredModel');

const emit = defineEmits<{
  'click-marker': [project: Project];
  'map-loaded': [void];
  'project-selected': [project: Project];
  'project-view-units': [project: Project];
}>();

// Map references and state
const googleMap = ref<InstanceType<typeof GoogleMap>>();
const mapRef = ref<google.maps.Map>();
const mapIsLoaded = ref(false);
const mapZoom = ref(7);
const items = ref<Project[]>(props.projects);

// InfoWindow state
let infoWindow: google.maps.InfoWindow | undefined;

const markerElements = ref<HTMLDivElement[]>([]);
const { getStartingPrice } = useProject();
const { getProjectWizardUrl: getRentalProjectWizardUrl } = useRentalLocalizedUrls();
const { getProjectWizardUrl: getPurchaseProjectWizardUrl } = usePurchaseLocalizedUrls();
const { filterValidCoordinates, calculateCenter } = useCoordinates();

// Utiliser la fonction appropriée selon le type de listing
const getProjectWizardUrl = (project: Project) => {
  if (props.listingType === ListingTypeEnum.Sale) {
    return getPurchaseProjectWizardUrl(project);
  } else if (props.listingType === ListingTypeEnum.Rental) {
    return getRentalProjectWizardUrl(project);
  }

  throw new Error('Unsupported listing type');
};

// Filtrer les projets avec des coordonnées valides
const validProjects = filterValidCoordinates(items);

// Centre par défaut pour le Québec si aucun projet valide
const defaultCoordinates = { lat: 46.8139, lng: -71.2080 }; // Québec, Canada

// Calculer le centre de tous les projets valides
const mapCenter = computed(() => {
  if (!validProjects.value.length) {
    return defaultCoordinates;
  }
  return calculateCenter(validProjects.value);
});

// Formatage du contenu de l'InfoWindow
function formatInfoWindowContent(project: Project): string {
  const displayPrice = getStartingPrice(project, props.listingType);
  const projectImageUrl = project.planImageUrl ? getImageUrlWithCustomWidth(project.planImageUrl, 300) : null;
  const projectUrl = getProjectWizardUrl(project);

  return `
    <div class="project-info-window">
      <div class="project-info-title">
        <h3>${project.localizedName || project.name}</h3>
      </div>
      <div class="project-info-content">
        <a href="${projectUrl}">
          <div class="project-info-image">
            ${projectImageUrl ? `<img src="${projectImageUrl}" alt="${project.localizedName || project.name}" class="project-info-image" />` : ''}
          </div>
          <div class="project-info-location">
            ${project.localizedRegionName ? `<div class="project-info-region">${project.localizedRegionName}</div>` : ''}
          </div>
          <div class="project-info-stats">
            <div class="project-info-stat">
              <span class="project-info-stat-label">${t('browsingApp.startingAt')}</span>
              <span class="project-info-stat-value">${displayPrice}</span>
            </div>
            <div class="project-info-stat">
              <span class="project-info-stat-label">${t('browsingApp.units.availableUnits', project.availableUnits ?? 0)}</span>
              <span class="project-info-stat-value">${project.availableUnits ?? 0}</span>
            </div>
          </div>
          <span class="project-info-button">
            ${t('browsingApp.actions.chooseThisProject')}
            <span class="arrow">
              <svg width="16" height="16" viewBox="0 0 17 14" fill="none">
                <path d="M9.60179 0.0012207L8.32797 1.25257L12.9042 5.74763H0.832031V7.51625H12.9042L8.32797 12.0122L9.60179 13.2631L16.3518 6.63216L9.60179 0.0012207Z" fill="currentColor"/>
              </svg>
            </span>
          </span>
        </a>
      </div>
    </div>
  `;
}

function getMarkerOptions(project: Project): google.maps.marker.AdvancedMarkerElementOptions {
  const displayPrice = getStartingPrice(project, props.listingType, false);
  
  const marker = buildPriceMarker(displayPrice, {
    itemId: project.id,
    mouseenterHandler: (event: MouseEvent) => {
      onMouseEnterMarker(event, project);
    },
    mouseleaveHandler: onMouseLeaveMarker
  });

  if(!markerElements.value.some(el => el.dataset.itemId === project.id.toString())) {
    markerElements.value.push(marker);
  }

  return {
    position: { lat: project.latitude, lng: project.longitude },
    title: project.name,
    content: marker,
    gmpClickable: true,
  };
}

function handleMarkerClick({ domEvent }: { domEvent: PointerEvent }, item: Project) {
  const { target } = domEvent;
  const marker = (target as SVGElement).closest('gmp-advanced-marker') as HTMLButtonElement;
  marker.setAttribute('aria-expanded', 'true');
  
  // Créer l'InfoWindow si elle n'existe pas
  if (!infoWindow && mapRef.value) {
    infoWindow = new google.maps.InfoWindow();
  }
  
  if (infoWindow && mapRef.value) {
    // Configurer le contenu et la position
    infoWindow.setContent(formatInfoWindowContent(item));
    infoWindow.setPosition({ lat: item.latitude, lng: item.longitude });
    infoWindow.open(mapRef.value);
    
    // Centrer la carte sur le marqueur
    mapRef.value.panTo({ lat: item.latitude, lng: item.longitude });
  }
  
  model.value = item;
  emit('click-marker', item);
  emit('project-selected', item);
}

function onMouseEnterMarker(event: MouseEvent, project: Project) {
  const { target } = event;
  setHoveredState(target as HTMLDivElement, true);
  hoveredModel.value = project;
}

function onMouseLeaveMarker(event: MouseEvent) {
  hoveredModel.value = undefined;
}

function setHoveredState(marker: HTMLDivElement, hovered: boolean) {
  const advancedMarkerElement = marker.closest('gmp-advanced-marker') as HTMLButtonElement;
  advancedMarkerElement.dataset.hoveredItem = hovered ? 'true' : 'false';
}

function fitBounds() {
  if (!mapRef.value || !validProjects.value.length) return;
  
  const map = mapRef.value;
  const bounds = new google.maps.LatLngBounds();
  
  // Étendre les bounds pour chaque projet valide
  validProjects.value.forEach(project => {
    const position = { lat: project.latitude, lng: project.longitude };
    bounds.extend(position);
  });
  
  if (!bounds.isEmpty()) {
    // Nettoyer les anciens listeners pour éviter les conflits
    google.maps.event.clearListeners(map, 'bounds_changed');
    google.maps.event.clearListeners(map, 'idle');
    
    // Appliquer les bounds avec padding
    const padding = { top: 50, right: 50, bottom: 50, left: 50 };
    map.fitBounds(bounds, padding);

    // Ajuster le zoom après que les bounds soient appliqués
    const boundsListener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const currentZoom = map.getZoom();
      if (currentZoom !== undefined) {
        const MIN_ZOOM_LEVEL = 3;
        const MAX_ZOOM_LEVEL = 15;
        
        if (currentZoom > MAX_ZOOM_LEVEL) {
          map.setZoom(MAX_ZOOM_LEVEL);
        } else if (currentZoom < MIN_ZOOM_LEVEL) {
          map.setZoom(MIN_ZOOM_LEVEL);
        }
        
        // Mettre à jour la ref reactive
        mapZoom.value = map.getZoom() || 7;
      }
    });

    // Backup: listener 'idle' au cas où 'bounds_changed' ne se déclenche pas
    const idleListener = google.maps.event.addListenerOnce(map, 'idle', () => {
      const currentZoom = map.getZoom();
      if (currentZoom !== undefined) {
        mapZoom.value = currentZoom;
      }
    });
  }
}

// Watch for changes in items
watch(() => items.value, () => {
  if (mapIsLoaded.value) {
    nextTick(() => {
      fitBounds();
    });
  }
}, { deep: true });

// Watch for changes in valid projects to refit bounds
watch(() => validProjects.value, (newValidProjects, oldValidProjects) => {
  if (mapIsLoaded.value) {
    nextTick(() => {
      // Délai pour s'assurer que les marqueurs sont rendus
      setTimeout(() => {
        if (newValidProjects.length > 0) {
          fitBounds();
        } else {
          // Si pas de projets valides, centrer sur Québec
          if (mapRef.value) {
            mapRef.value.setCenter(defaultCoordinates);
            mapRef.value.setZoom(7);
            mapZoom.value = 7;
          }
        }
      }, 150); // Délai pour une meilleure stabilité
    });
  }
}, { deep: true, immediate: false });

// Update items when projects prop changes
watch(() => props.projects, (newProjects) => {
  items.value = newProjects;
}, { deep: true });

watch(() => googleMap.value?.ready, (isReady) => {
  if (isReady) {
    mapIsLoaded.value = true;
    mapRef.value = googleMap.value?.map;
    emit('map-loaded');
    nextTick(() => {
      fitBounds();
    });
  }
}, {
  immediate: true, deep: true});
</script>

<template>
  <div class="projectGoogleMap">
    <GoogleMap ref="googleMap"
               api-key="AIzaSyA5oNLf2Z4LkCZgd58Hei7FehuAo1vst2A"
               map-id="r4a75aac462151035"
               class="projectGoogleMap-container"
               :center="mapCenter"
               :zoom="mapZoom"
               :street-view-control="false"
               :aria-label="ariaLabel">
      <AdvancedMarker v-for="project in validProjects"
                      :key="project.id"
                      :options="getMarkerOptions(project)"
                      @click="handleMarkerClick($event, project)">
      </AdvancedMarker>
    </GoogleMap>
  </div>
</template>

<style lang="scss">
.projectGoogleMap {
  width: 100%;
  height:100%;
  position: relative;
  border-radius: var(--radius-md);
  border: var(--contentSizeBorderWidth) solid var(--contentColorBorder);
  overflow: hidden;
}

.projectGoogleMap-container {
  width: 100%;
  height: 100%;
}

/* Marqueur de prix en forme de pilule */
.hg-price-marker {
  position: relative;
  z-index: 10;
  cursor: pointer;
  
  &-content {
    padding: 0.8rem 1.6rem;
    background: #ffffff;
    border-radius: 2.0rem;
    box-shadow: 
      0 4px 12px rgba(0, 0, 0, 0.3),
      0 2px 6px rgba(0, 0, 0, 0.2),
      0 0 0 1px rgba(255, 255, 255, 0.9) inset;
    font-size: 1.4rem;
    font-weight: 600;
    color: var(--gray-950);
    white-space: nowrap;
    border: 2px solid var(--primary-500);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: drop-shadow(0 0 4px rgba(255, 255, 255, 0.5));
    
    &:hover {
      box-shadow: 
        0 8px 20px rgba(0, 0, 0, 0.4),
        0 4px 12px rgba(0, 0, 0, 0.3),
        0 0 0 1px rgba(255, 255, 255, 1) inset;
      transform: translateY(-2px) scale(1.02);
      filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.7));
    }
  }
}

/* Boutons superflus de la carte Google */
.gm-style-mtc-bbw {
  display: none;
}

/* États */
gmp-advanced-marker {
  &:has([data-hovered-item="true"]) {
    scale: 1.05;
  }
}
.gm-style{

  > div:first-child{
      &:before{
        content: "";
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        pointer-events: none;
        background-color: rgba(0, 0, 0, 0.18);
        z-index: 2;
      }
  }

  .gm-style-iw-a{
    .gm-style-iw{
      border: 2px solid var(--gray-200);
      border-radius: .8rem;
      background: #fff;
      padding: 1.6rem!important;
      outline: 4px solid transparent;
      box-shadow: none;
      font-size: 1.6rem;
    
      .gm-style-iw-ch{
        display:none;
      }
      .gm-style-iw-chr{
        button{
          position:absolute!important;
          top:0;
          right:0;
        }
      }

      .gm-style-iw-d{
        overflow: unset!important;
      }
    }
  }

  .gm-style-iw-t{
    bottom:5.0rem!important;
  }

  .project-info-window{
    line-height: 1.5;

    .project-info-title{
      padding-right: 2rem;
      max-width: 300px;
      
      h3{
        font-size: 2rem;
        font-weight: 600;
        font-family: Inter, Roboto, sans-serif;
        margin: 0;
        margin-bottom: 1.6rem;
      }
    }
    .project-info-image{
      overflow: clip;
      width: 100%;
      height: auto;
      aspect-ratio: 16 / 9;
      border-radius: 8px;
    }
    .project-info-content{
      font-family: Inter, Roboto, sans-serif;

      a{
        text-decoration: none;
        color: var(--gray-950);

        &:hover{
          .arrow{
            transform: translate(2px);
          }
        }
      }

      .project-info-location{
        margin: 1.6rem 0;
      }
      .project-info-region{
        font-size: 1.8rem;
        font-weight: 600;
        margin-bottom: .125rem;
      }
      .project-info-address{
        font-size: 1.6rem;
        font-weight: 500;
      }
      .project-info-stats{
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 1.6rem;
        margin-bottom: 1.6rem;

        .project-info-stat-label{
          font-size: 1.4rem;
          display:block;
        }

        .project-info-stat-value{
          display:block;
          font-size: 1.6rem;
          font-weight: 700;
          margin: 0;
        }
      }

      .project-info-button{
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: .5rem;
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--primary-600);
        text-decoration: none;

        .arrow{
          font-size: 1.25rem;
          transition: transform .2s;
        }
      }
    }
  }
  .gm-style-iw-tc{
    width: 2.4rem;
    height: 2.4rem;
    border: 2px solid var(--gray-200);
    border-width: 0 2px 2px 0;
    background-color: #fff;
    filter: none;
    transform: rotate(45deg) translateX(-50%);
    left: 0;
    margin: 0 auto;
    top: -5px;
    z-index: 0;

    &:after{
      display: none !important;
    }
  }
}
@media screen and (max-width: 1024px){
  .projectGoogleMap {
    border-radius: 0;
  }
}
</style>